/**
 * ItemCardPreviewModal
 * 
 * Displays PDF in an iframe/embed with Print and Download buttons.
 * Resilient to browser extensions that block blob URL navigation.
 */
import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Printer, Download, X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ItemCardPreviewModal({ 
  open, 
  onClose, 
  blobUrl, 
  filename = "item-card.pdf",
  itemName = "Item Card"
}) {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open && blobUrl) {
      setLoading(true);
      setLoadError(false);
    }
  }, [open, blobUrl]);

  // Cleanup blob URL when modal closes
  useEffect(() => {
    return () => {
      // Cleanup handled by parent component
    };
  }, []);

  const handleIframeLoad = () => {
    setLoading(false);
    setLoadError(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setLoadError(true);
  };

  const handlePrint = () => {
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } else {
        toast.info("Use Download then print from the file", { duration: 4000 });
      }
    } catch (error) {
      console.error("Print error:", error);
      toast.info("Print blocked by browser. Use Download then print from the file.", { duration: 4000 });
    }
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    
    try {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Download started");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Download failed");
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg font-heading">
            <Printer className="w-5 h-5 text-primary" />
            {itemName}
          </DialogTitle>
        </DialogHeader>
        
        {/* PDF Preview Area */}
        <div className="flex-1 relative bg-muted/30 overflow-hidden min-h-0">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Loading PDF...</span>
              </div>
            </div>
          )}
          
          {loadError && (
            <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
              <div className="flex flex-col items-center gap-4 text-center px-6">
                <AlertCircle className="w-12 h-12 text-yellow-500" />
                <div>
                  <p className="text-lg font-medium">PDF Preview Unavailable</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your browser may be blocking the preview. Use the Download button below.
                  </p>
                </div>
                <Button onClick={handleDownload} className="mt-2">
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
          
          {blobUrl && (
            <iframe
              ref={iframeRef}
              src={blobUrl}
              className="w-full h-full border-0"
              title="PDF Preview"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          )}
        </div>
        
        {/* Footer with actions */}
        <DialogFooter className="px-6 py-4 border-t border-border shrink-0 flex-row justify-between sm:justify-between">
          <Button variant="outline" onClick={handleClose}>
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
